import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronDown, Heart, Minus, Plus, ShieldCheck, Star, Truck, RefreshCcw, Sparkles } from "lucide-react";
import { api, formatZAR, formatApiError, priceFor } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";

const FAQS = [
  { q: "How long does this wig last?", a: "With proper care, our human-hair wigs last 12–18 months and human-blends 6–9 months. We include a full care guide." },
  { q: "Is this beginner friendly?", a: "Yes – every glueless and headband piece in our store comes pre-styled, pre-plucked and elastic-banded so you can wear it straight from the box." },
  { q: "Can I dye or curl this wig?", a: "Our 100% human-hair pieces can be dyed and heat-styled up to 180°C. Synthetic blends should be styled with heat-resistant tools at low settings only." },
  { q: "What if it doesn't suit me?", a: "Uncut, unworn lace wigs can be exchanged within 30 days. See our Refund Policy for full details." },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [related, setRelated] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [length, setLength] = useState(null);
  const [density, setDensity] = useState(null);
  const [color, setColor] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, title: "", body: "" });

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setLength(r.data.length_inches);
      setDensity(r.data.density);
      setColor(r.data.colors?.[0] || "");
    }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    api.get("/reviews", { params: { product_id: product.id } }).then((r) => setReviews(r.data));
    api.get("/products", { params: { category: product.category, limit: 8 } }).then((r) => {
      setRelated(r.data.filter((p) => p.slug !== product.slug).slice(0, 4));
    });
  }, [product]);

  const lengthOptions = useMemo(() => {
    if (!product) return [];
    const base = product.length_inches;
    return [-4, -2, 0, 2, 4, 6].map((d) => Math.max(8, base + d));
  }, [product]);
  const densityOptions = useMemo(() => ["150%", "180%", "200%", "250%"], []);

  const avgRating = useMemo(() => {
    if (!reviews.length) return 5;
    return (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1);
  }, [reviews]);

  const images = product?.images?.length ? product.images : product ? [product.image] : [];

  const dynamicPrice = useMemo(() => product ? priceFor(product, length, density) : 0, [product, length, density]);
  const dynamicCompareAt = useMemo(() => {
    if (!product?.compare_at_price) return null;
    const ratio = product.compare_at_price / product.price;
    return Math.round((dynamicPrice * ratio) / 50) * 50 - 1;
  }, [product, dynamicPrice]);

  const handleAdd = () => {
    if (!product) return;
    addItem(product, { quantity, length, density, color, price: dynamicPrice });
    toast.success(`${product.title} added to bag`);
  };

  const handleBuyNow = () => {
    if (!product) return;
    addItem(product, { quantity, length, density, color, price: dynamicPrice });
    window.location.href = "/checkout";
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { ...reviewForm, product_id: product.id });
      const r = await api.get("/reviews", { params: { product_id: product.id } });
      setReviews(r.data);
      setReviewForm({ name: "", rating: 5, title: "", body: "" });
      toast.success("Review submitted. Thank you!");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!product) {
    return <div data-testid="product-loading" className="max-w-7xl mx-auto px-6 py-20 text-center small-label">Loading…</div>;
  }

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 py-10 lg:py-16">
      <nav className="small-label text-stone mb-6">
        <Link to="/" className="hover:text-charcoal">Home</Link> ·{" "}
        <Link to="/shop" className="hover:text-charcoal">Shop</Link> ·{" "}
        <span className="text-charcoal">{product.title}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div>
          <div
            className="relative aspect-[3/4] overflow-hidden rounded-3xl bg-nude cursor-zoom-in"
            onMouseEnter={() => setZoom(true)}
            onMouseLeave={() => setZoom(false)}
            data-testid="product-gallery-main"
          >
            <img
              src={images[activeImage]}
              alt={product.title}
              className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 ${zoom ? "scale-125" : "scale-100"}`}
            />
            <span className="absolute top-4 left-4 bg-ivory/90 backdrop-blur text-[10px] uppercase tracking-luxe px-2.5 py-1 rounded-full">
              {product.badges?.[0] || product.lace_type}
            </span>
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((src, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`relative w-20 h-24 rounded-xl overflow-hidden border ${i === activeImage ? "border-charcoal" : "border-stone-line"}`}
                >
                  <img src={src} alt="thumb" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <p className="small-label">{product.texture}</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-2 leading-tight">{product.title}</h1>
          <div className="flex items-center gap-3 mt-3 text-sm">
            <div className="flex gap-0.5 text-gold">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-current" />
              ))}
            </div>
            <span className="text-stone text-xs">{avgRating} · {reviews.length} reviews</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="font-serif text-3xl" data-testid="product-detail-price">{formatZAR(dynamicPrice)}</span>
            {dynamicCompareAt && dynamicCompareAt > dynamicPrice && (
              <>
                <span className="text-stone line-through text-sm">{formatZAR(dynamicCompareAt)}</span>
                <span className="text-xs uppercase tracking-luxe bg-charcoal text-ivory rounded-full px-2 py-0.5">
                  Save {Math.round(((dynamicCompareAt - dynamicPrice) / dynamicCompareAt) * 100)}%
                </span>
              </>
            )}
          </div>
          <p className="text-xs text-stone mt-2">or 3 interest-free payments of {formatZAR(Math.round(dynamicPrice / 3))} with Payflex.</p>

          <p className="mt-6 text-stone leading-relaxed">{product.description}</p>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mt-6">
            {product.badges?.map((b) => (
              <span key={b} className="text-[10px] uppercase tracking-luxe bg-nude border border-stone-line rounded-full px-3 py-1">
                <Sparkles className="w-3 h-3 inline mr-1 text-gold" />{b}
              </span>
            ))}
          </div>

          {/* Selectors */}
          <div className="mt-8 space-y-6">
            <div>
              <p className="small-label mb-2">Length</p>
              <div className="flex flex-wrap gap-2">
                {lengthOptions.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLength(l)}
                    data-testid={`length-${l}`}
                    className={`text-xs uppercase tracking-luxe rounded-full border px-4 py-2 ${
                      length === l ? "bg-charcoal text-ivory border-charcoal" : "border-stone-line hover:bg-nude"
                    }`}
                  >
                    {l}″
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="small-label mb-2">Density</p>
              <div className="flex flex-wrap gap-2">
                {densityOptions.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDensity(d)}
                    data-testid={`density-${d}`}
                    className={`text-xs uppercase tracking-luxe rounded-full border px-4 py-2 ${
                      density === d ? "bg-charcoal text-ivory border-charcoal" : "border-stone-line hover:bg-nude"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            {product.colors?.length > 0 && (
              <div>
                <p className="small-label mb-2">Colour: <span className="text-charcoal normal-case tracking-normal">{color}</span></p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      data-testid={`color-${c.toLowerCase().replace(/ /g, "-")}`}
                      className={`text-xs tracking-luxe uppercase rounded-full border px-4 py-2 ${
                        color === c ? "bg-charcoal text-ivory border-charcoal" : "border-stone-line"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="small-label mb-2">Quantity</p>
              <div className="inline-flex items-center border border-stone-line rounded-full">
                <button data-testid="qty-decrement" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="p-3"><Minus className="w-4 h-4" /></button>
                <span className="px-5">{quantity}</span>
                <button data-testid="qty-increment" onClick={() => setQuantity((q) => q + 1)} className="p-3"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button data-testid="add-to-cart-button" onClick={handleAdd} className="btn-outline">
              <Heart strokeWidth={1.5} className="w-4 h-4 mr-2" /> Add to Bag
            </button>
            <button data-testid="buy-now-button" onClick={handleBuyNow} className="btn-primary">Buy Now</button>
          </div>

          {/* Inline trust */}
          <div className="mt-8 grid grid-cols-3 gap-4 text-center">
            <div>
              <Truck strokeWidth={1.5} className="w-5 h-5 mx-auto" />
              <p className="text-[11px] tracking-luxe uppercase mt-2 text-stone">Free Over R2 000</p>
            </div>
            <div>
              <ShieldCheck strokeWidth={1.5} className="w-5 h-5 mx-auto" />
              <p className="text-[11px] tracking-luxe uppercase mt-2 text-stone">Secure SA Pay</p>
            </div>
            <div>
              <RefreshCcw strokeWidth={1.5} className="w-5 h-5 mx-auto" />
              <p className="text-[11px] tracking-luxe uppercase mt-2 text-stone">30-Day Exchange</p>
            </div>
          </div>

          {/* Specs */}
          <div className="mt-10 border-t border-stone-line pt-6">
            <p className="small-label mb-3">Specifications</p>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-stone">Texture</dt><dd>{product.texture}</dd>
              <dt className="text-stone">Lace</dt><dd>{product.lace_type}</dd>
              <dt className="text-stone">Density</dt><dd>{product.density}</dd>
              <dt className="text-stone">Cap Size</dt><dd>{product.cap_size}</dd>
              <dt className="text-stone">Hair Type</dt><dd>{product.hair_type}</dd>
              <dt className="text-stone">Length</dt><dd>{product.length_inches}″</dd>
            </dl>
          </div>

          {/* FAQs */}
          <div className="mt-10 border-t border-stone-line pt-6">
            <p className="small-label mb-4">FAQ</p>
            <div className="divide-y divide-stone-line">
              {FAQS.map((f, i) => (
                <div key={i} className="py-4">
                  <button
                    data-testid={`faq-${i}`}
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="flex items-center justify-between w-full text-left text-sm"
                  >
                    <span>{f.q}</span>
                    <ChevronDown strokeWidth={1.5} className={`w-4 h-4 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && <p className="text-sm text-stone mt-3 leading-relaxed">{f.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <section className="mt-24 border-t border-stone-line pt-16">
        <div className="grid lg:grid-cols-3 gap-10">
          <div>
            <p className="small-label">Customer reviews</p>
            <h2 className="font-serif text-3xl mt-3">{avgRating} <span className="text-stone text-lg">/ 5</span></h2>
            <p className="text-sm text-stone mt-2">{reviews.length} verified reviews</p>
            <form onSubmit={submitReview} className="mt-8 space-y-3" data-testid="review-form">
              <input
                required
                placeholder="Your name"
                value={reviewForm.name}
                onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                data-testid="review-name-input"
                className="w-full bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-charcoal"
              />
              <select
                value={reviewForm.rating}
                onChange={(e) => setReviewForm({ ...reviewForm, rating: Number(e.target.value) })}
                data-testid="review-rating-select"
                className="w-full bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm"
              >
                {[5,4,3,2,1].map((n) => <option key={n} value={n}>{`${n} stars`}</option>)}
              </select>
              <input
                required placeholder="Title"
                value={reviewForm.title}
                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                data-testid="review-title-input"
                className="w-full bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm"
              />
              <textarea
                required rows={4} placeholder="What did you think?"
                value={reviewForm.body}
                onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })}
                data-testid="review-body-input"
                className="w-full bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm"
              />
              <button data-testid="review-submit-button" className="btn-primary w-full">Submit review</button>
            </form>
          </div>
          <div className="lg:col-span-2 space-y-6">
            {reviews.length === 0 ? (
              <p className="text-stone">Be the first to review this piece.</p>
            ) : reviews.map((r) => (
              <div key={r.id} className="border border-stone-line rounded-2xl p-6">
                <div className="flex gap-0.5 text-gold">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
                </div>
                <p className="font-serif text-lg mt-2">{r.title}</p>
                <p className="text-sm text-stone mt-2 leading-relaxed">{r.body}</p>
                <p className="small-label mt-3">{r.name} {r.verified && <span className="text-gold ml-2">✓ Verified</span>}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="mt-24 border-t border-stone-line pt-16">
          <h2 className="font-serif text-3xl">You may also love</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 lg:gap-x-8 gap-y-10 mt-8">
            {related.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
          </div>
        </section>
      )}
    </div>
  );
}
